import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MockupsPanel } from '../../components/mockups-panel/mockups-panel';
import {
  StakeholdersPanel,
  type NewStakeholder,
  type StakeholderRoleChange,
} from '../../components/stakeholders-panel/stakeholders-panel';
import { PagesPanel } from '../../components/pages-panel/pages-panel';
import { UploadMockupsDrawer } from '../../components/upload-mockups-drawer/upload-mockups-drawer';
import { AddAssetDrawer } from '../../components/add-asset-drawer/add-asset-drawer';
import { EmbedSettingsDrawer } from '../../components/embed-settings-drawer/embed-settings-drawer';
import { ManualEmailDialog } from '../../components/manual-email-dialog/manual-email-dialog';
import { EmailLogPanel } from '../../components/email-log-panel/email-log-panel';
import { AiActivityChip } from '../../components/ai-activity-chip/ai-activity-chip';
import {
  StakeholderPreferencesDialog,
  type PreferencesMember,
} from '../../components/stakeholder-preferences-dialog/stakeholder-preferences-dialog';
import {
  catchError,
  distinctUntilChanged,
  firstValueFrom,
  forkJoin,
  map,
  merge,
  of,
  startWith,
  switchMap,
  takeWhile,
  tap,
  timer,
  type Observable,
} from 'rxjs';
import {
  AiFeedbackService,
  type AiReview,
  type AiReviewAck,
  AiReviewService,
  type Comment,
  CommentAuthorType,
  CommentService,
  type UnreadCommentCounts,
  FeedbackCategory,
  type FeedbackScoreBand,
  FeedbackStatus,
  type Feedback,
  type FeedbackRound,
  type FeedbackRoundAsset,
  type FeedbackRoundPage,
  FeedbackRoundService,
  type FeedbackRoundUser,
  FeedbackRoundStatus,
  FeedbackService,
  FeedbackType,
  FilesService,
  GOOGLE_RECONNECT_REASONS,
  type GoogleConnection,
  GoogleConnectionService,
  GoogleExportService,
  FeedbackAuditService,
  type FeedbackAuditRun,
  type ListFeedbackQuery,
  PageStatus,
  type PagesReadyForReviewResult,
  type Paginated,
  RoundAssetType,
  ScreenType,
} from '@brafton/feedback-api';
import {
  AiAssistant,
  type AiPrompt,
  type AiPromptQuestion,
  type AiPromptSubmission,
  type DisregardReason,
  DisregardReasonDialog,
  disregardReasonComment,
  ExportFeedback,
  type ExportFilterChip,
  type ExportFormat,
  type FeedbackDetail,
  FeedbackDetailDrawer,
  type CommentRecipientOption,
  type CommentThreadEntry,
  type FeedbackDetailUser,
  type NewComment,
  FeedbackTable,
  type FeedbackFilters,
  type FeedbackRow,
  type FeedbackBulkStatusChange,
  type FeedbackRowStatusChange,
  type FilterOption,
  LoadingBar,
  NewFeedbackDrawer,
  type NewFeedbackSubmission,
  type ProjectIcon,
  ProgressBar,
  type SortSpec,
  StatusChip,
  pickProjectIcon,
} from '@brafton/feedback-components';
import { CurrentUserService } from '../../core/current-user.service';
import { pageReviewLink } from '../../core/page-review-link';
import {
  GOOGLE_DOCUMENTS_READ_SCOPE,
  GOOGLE_SPREADSHEETS_READ_SCOPE,
  GoogleAuthCodeError,
  GoogleAuthCodeService,
} from '../../core/google-auth-code.service';

const EMPTY_FEEDBACK_PAGE: Paginated<Feedback> = {
  page: 1,
  perPage: 0,
  total: 0,
  lastPage: 1,
  results: [],
};

/**
 * Escapes a single CSV cell per RFC 4180: values containing a comma, double
 * quote, or line break are wrapped in double quotes, with inner quotes doubled.
 */
function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Serializes a table (header row first) as RFC 4180 CSV text. */
function toCsv(table: string[][]): string {
  return table.map((tableRow) => tableRow.map(csvCell).join(',')).join('\r\n');
}

/** The "None" answer to the feedback audit's optional document questions. */
const NO_DOCUMENTS = 'None';

/** How often a running feedback audit is checked for its finished report. */
const AUDIT_POLL_INTERVAL_MS = 5000;

/** How often the project's running AI processes are checked, for the activity chip. */
const AI_ACTIVITY_POLL_INTERVAL_MS = 15000;

/** Google Doc links in a free-text answer (one or more, separated by spaces, commas or lines). */
function googleDocLinks(answer: string | undefined): string[] {
  if (!answer || answer.trim().toLowerCase() === NO_DOCUMENTS.toLowerCase()) {
    return [];
  }
  return answer.split(/[\s,]+/).filter((candidate) => /^https?:\/\/docs\.google\.com\/document\//i.test(candidate));
}

/** How many of the round's pages the AI prompts' page pickers list. */
const AI_PAGE_OPTION_LIMIT = 100;

/** The Link Checker's / Spelling & Grammar check's "All pages (from the sitemap)" answer. */
const ALL_SITEMAP_PAGES = '__all_sitemap_pages__';

/** The Spelling & Grammar check's "A specific sitemap…" answer. */
const SPECIFIC_SITEMAP = '__specific_sitemap__';

/**
 * Whether a typed URL appears to be a sitemap rather than a page: it ends in
 * .xml / .xml.gz (sitemap.xml, post-sitemap.xml, sitemap_index.xml…), or its last
 * path segment is a bare `sitemap` / `sitemap_index` / `wp-sitemap`.
 */
function looksLikeSitemapUrl(url: string): boolean {
  let path: string;
  try {
    path = new URL(url.trim()).pathname;
  } catch {
    return false;
  }
  return /\.xml(\.gz)?$/i.test(path) || /\/(wp-)?sitemap(_index)?$/i.test(path);
}

/**
 * What a Link Checker / Spelling & Grammar answer set points at: one page, the
 * whole site's sitemap, or one sitemap (chosen, or a typed URL that looks like one).
 */
function siteCheckTarget(answers: Record<string, string>): {
  scope: 'page' | 'sitemap';
  url?: string;
  sitemapUrl?: string;
} {
  const chosen = (answers['url'] ?? '').trim();
  if (chosen === ALL_SITEMAP_PAGES) {
    return { scope: 'sitemap' };
  }
  if (chosen === SPECIFIC_SITEMAP) {
    return { scope: 'sitemap', sitemapUrl: (answers['sitemapUrl'] ?? '').trim() || undefined };
  }
  if (looksLikeSitemapUrl(chosen)) {
    return { scope: 'sitemap', sitemapUrl: chosen };
  }
  return { scope: 'page', url: chosen };
}

/** Whether the answers point at a sitemap the user typed (or chose "A specific sitemap…" for). */
function isTypedSitemap(answers: Record<string, string>): boolean {
  return siteCheckTarget(answers).sitemapUrl !== undefined;
}

/** The note under a checker's page question: a sitemap URL works too. */
const SITEMAP_URL_HINT =
  'You may also type a URL below — a page, or a sitemap (e.g. https://example.com/sitemap.xml or post-sitemap.xml) to check every page in it.';

/** Asked when the whole site's sitemap is chosen. */
const ALL_PAGES_EXCLUSIONS_QUESTION =
  'Should anything in the sitemap be skipped? Say it in your own words (e.g. "skip the blogs, we don’t need those"), or choose None.';

/** Asked when the typed URL looks like a sitemap. */
const TYPED_SITEMAP_EXCLUSIONS_QUESTION =
  'That looks like a sitemap, so every page in it will be checked. Should anything in it be skipped? Say it in your own words (e.g. "skip the blogs, we don’t need those"), or choose None.';

/** The export formats the API creates in the user's Google Drive. */
type GoogleExportKind = Extract<ExportFormat, 'gsheet' | 'gdoc'>;

/** A created Google file, normalized across Sheets and Docs for the result toast. */
interface GoogleExportResult {
  url: string;
  itemCount: number;
  /** Extra sentence for the toast (e.g. screenshots that were linked instead). */
  note: string;
}

const KNOWN_FEEDBACK_STATUSES = new Set<string>(Object.values(FeedbackStatus));

/**
 * The status filter as the API takes it: one status, or several when the filter holds
 * a comma-separated list (from an email link). Unknown values are dropped, so a
 * hand-edited link can't make the list request fail.
 */
function toStatusFilter(statusFilter: string | undefined): FeedbackStatus[] | undefined {
  const statuses = (statusFilter ?? '')
    .split(',')
    .map((statusValue) => statusValue.trim())
    .filter((statusValue) => KNOWN_FEEDBACK_STATUSES.has(statusValue)) as FeedbackStatus[];

  return statuses.length ? statuses : undefined;
}

/**
 * The table's created-day range (`YYYY-MM-DD`, as the date inputs give it) as the
 * list query's ISO instants: the From day's local midnight and the To day's last
 * local millisecond, so the range matches the created dates the viewer sees.
 * A malformed day is left out, so a hand-edited link can't fail the request.
 */
function toCreatedRange(filters: FeedbackFilters): Pick<ListFeedbackQuery, 'createdFrom' | 'createdTo'> {
  const localDayBound = (dayText: string | undefined, isEndOfDay: boolean): string | undefined => {
    const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayText ?? '');
    if (!dayMatch) {
      return undefined;
    }
    const [, yearText, monthText, dayOfMonthText] = dayMatch;
    const localDay = isEndOfDay
      ? new Date(Number(yearText), Number(monthText) - 1, Number(dayOfMonthText), 23, 59, 59, 999)
      : new Date(Number(yearText), Number(monthText) - 1, Number(dayOfMonthText), 0, 0, 0, 0);
    return Number.isNaN(localDay.getTime()) ? undefined : localDay.toISOString();
  };

  return {
    createdFrom: localDayBound(filters.createdFrom, false),
    createdTo: localDayBound(filters.createdTo, true),
  };
}

/** How often the feedback list re-polls the server for changes. */
const FEEDBACK_POLL_INTERVAL_MS = 60_000;

/**
 * A compact fingerprint of a feedback page used to decide whether a poll found
 * any change worth surfacing — total count plus each item's id, status, and last
 * change time. If two consecutive polls produce the same signature, nothing is
 * pushed to the table.
 */
function feedbackSignature(page: Paginated<Feedback>): string {
  const items = page.results
    .map((item) => `${item.id}:${item.status}:${item.updatedAt ?? item.createdAt ?? ''}`)
    .join(',');
  return `${page.total}|${items}`;
}

/**
 * Project (feedback-round) detail page: the header strip (back link, project
 * name + identifiers, status chip, progress bar) and the paginated feedback
 * table. The header round and the feedback list are fetched separately — the
 * round without its feedback, the feedback via the paginated feedback endpoint.
 */
@Component({
  selector: 'app-project-detail-page',
  imports: [
    RouterLink,
    MatIconModule,
    StatusChip,
    ProgressBar,
    LoadingBar,
    FeedbackTable,
    FeedbackDetailDrawer,
    NewFeedbackDrawer,
    MockupsPanel,
    StakeholdersPanel,
    PagesPanel,
    UploadMockupsDrawer,
    AddAssetDrawer,
    EmbedSettingsDrawer,
    ManualEmailDialog,
    EmailLogPanel,
    AiActivityChip,
    StakeholderPreferencesDialog,
    AiAssistant,
    ExportFeedback,
    DisregardReasonDialog,
  ],
  templateUrl: './project-detail-page.html',
  styleUrl: './project-detail-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectDetailPage {
  private readonly feedbackRounds = inject(FeedbackRoundService);
  private readonly feedback = inject(FeedbackService);
  private readonly comments = inject(CommentService);
  private readonly aiFeedback = inject(AiFeedbackService);
  private readonly aiReviews = inject(AiReviewService);
  private readonly files = inject(FilesService);
  private readonly http = inject(HttpClient);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentUser = inject(CurrentUserService);
  private readonly googleAuthCode = inject(GoogleAuthCodeService);
  private readonly googleConnections = inject(GoogleConnectionService);
  private readonly googleExport = inject(GoogleExportService);
  private readonly feedbackAudits = inject(FeedbackAuditService);

  /** Feedback round id, bound from the `projects/:id` route param. */
  readonly id = input('');

  /**
   * Deep-link target, bound from the `?feedback={id}` query param (used by the
   * widget's "open in app" link). When present, that feedback's detail drawer is
   * opened on load. Aliased because `feedback` is the injected service name.
   */
  readonly feedbackParam = input('', { alias: 'feedback' });

  /** Page links in the table and drawer carry the widget parameter on client sites. */
  protected readonly pageReviewLink = pageReviewLink;

  /*
   * Feedback filters from the link's query parameters (e.g. the daily email's
   * `?status=action`), bound by the router. `status` may hold several comma-separated
   * values (`?status=needs_internal_review,needs_client_review`).
   */
  readonly statusParam = input('', { alias: 'status' });
  readonly urlParam = input('', { alias: 'url' });
  readonly categoryParam = input('', { alias: 'category' });
  readonly screenParam = input('', { alias: 'screen' });
  readonly typeParam = input('', { alias: 'type' });
  readonly scoreParam = input('', { alias: 'score' });
  readonly userParam = input('', { alias: 'user' });
  /** Created-day range from the link, `YYYY-MM-DD` like the table's date inputs. */
  readonly createdFromParam = input('', { alias: 'createdFrom' });
  readonly createdToParam = input('', { alias: 'createdTo' });

  /** The filters the link asks for; empty values are left out. */
  private readonly linkFilters = computed<FeedbackFilters>(() => {
    const linkFilters: FeedbackFilters = {
      status: this.statusParam(),
      url: this.urlParam(),
      category: this.categoryParam(),
      screen: this.screenParam(),
      type: this.typeParam(),
      score: this.scoreParam(),
      user: this.userParam(),
      createdFrom: this.createdFromParam(),
      createdTo: this.createdToParam(),
    };
    for (const filterKey of Object.keys(linkFilters) as (keyof FeedbackFilters)[]) {
      if (!linkFilters[filterKey]) {
        delete linkFilters[filterKey];
      }
    }
    return linkFilters;
  });

  /** Current feedback sort; defaults to newest-created first. */
  protected readonly sort = signal<SortSpec[]>([{ col: 'created', dir: 'desc' }]);

  /**
   * Active feedback filters (category/screen/status/type/score/url/user). Starts from
   * the link's query parameters and is re-seeded whenever they change, so a one-click
   * link from an email always opens on exactly those items; after that the filter
   * controls are free to change it.
   */
  protected readonly filters = linkedSignal<FeedbackFilters>(() => this.linkFilters());

  /** Category select options — the full enum, for stable server-side filtering. */
  protected readonly categoryFilterOptions: FilterOption[] = Object.values(FeedbackCategory).map(
    (value) => ({
      value,
      label: value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase()),
    }),
  );

  /** Current feedback page; resets to 1 when the round, sort, or filters change. */
  protected readonly feedbackPage = linkedSignal(() => {
    this.id();
    this.sort();
    this.filters();
    return 1;
  });

  // The round header (no feedback loaded — the table fetches that separately).
  private readonly fetchedRound = toSignal(
    toObservable(this.id).pipe(
      switchMap((roundId) =>
        roundId
          ? this.feedbackRounds.getFeedbackRound(roundId).pipe(
              map((round) => ({ loading: false, round: round as FeedbackRound | null })),
              catchError(() => of({ loading: false, round: null as FeedbackRound | null })),
              startWith({ loading: true, round: null as FeedbackRound | null }),
            )
          : of({ loading: false, round: null as FeedbackRound | null }),
      ),
    ),
    { initialValue: { loading: true, round: null as FeedbackRound | null } },
  );

  /** True while the round header is being fetched; the layout shows placeholders meanwhile. */
  protected readonly roundLoading = computed(() => this.fetchedRound().loading);

  // Locally-editable copy seeded from the fetch, for optimistic status changes.
  protected readonly round = linkedSignal(() => this.fetchedRound().round);

  /** Bumped when the Pages panel changes the round's pages, to refetch the AI page options. */
  private readonly aiPagesRefresh = signal(0);

  /**
   * The first AI_PAGE_OPTION_LIMIT pages of the round, for the AI prompts' page
   * pickers. Rounds can have tens of thousands of pages, so the pickers list a
   * slice; any other page can still be typed in as a URL.
   */
  private readonly aiPages = toSignal(
    toObservable(computed(() => ({ roundId: this.id(), refresh: this.aiPagesRefresh() }))).pipe(
      switchMap(({ roundId }) =>
        roundId
          ? this.feedbackRounds.listPages(roundId, { perPage: AI_PAGE_OPTION_LIMIT }).pipe(
              map((pagesPage) => pagesPage.results),
              catchError(() => of<FeedbackRoundPage[]>([])),
            )
          : of<FeedbackRoundPage[]>([]),
      ),
    ),
    { initialValue: [] as FeedbackRoundPage[] },
  );

  // Distinct URLs in the round, for the URL filter dropdown.
  protected readonly feedbackUrls = toSignal(
    toObservable(this.id).pipe(
      switchMap((roundId) =>
        roundId
          ? this.feedback.listFeedbackUrls(roundId).pipe(catchError(() => of<string[]>([])))
          : of<string[]>([]),
      ),
    ),
    { initialValue: [] as string[] },
  );

  // Distinct authors in the round (plus an AI reviewer option when present), for
  // the "filter by user" dropdown. Each option's value is the author email (or
  // 'ai'); the label is their display name.
  protected readonly feedbackUserOptions = toSignal(
    toObservable(this.id).pipe(
      switchMap((roundId) =>
        roundId
          ? this.feedback
              .listFeedbackUsers(roundId)
              .pipe(catchError(() => of<FilterOption[]>([])))
          : of<FilterOption[]>([]),
      ),
    ),
    { initialValue: [] as FilterOption[] },
  );

  /** Bumped right after an AI process is started, so the activity chip shows it at once. */
  private readonly aiActivityRefresh = signal(0);

  /** The project's AI processes in progress (from the AI review log), polled for the activity chip. */
  protected readonly runningAiReviews = toSignal(
    toObservable(computed(() => ({ roundId: this.id(), refresh: this.aiActivityRefresh() }))).pipe(
      switchMap(({ roundId }) =>
        roundId
          ? timer(0, AI_ACTIVITY_POLL_INTERVAL_MS).pipe(
              switchMap(() =>
                this.aiReviews.listAiReviews(roundId, 'in_progress').pipe(catchError(() => of<AiReview[]>([]))),
              ),
            )
          : of<AiReview[]>([]),
      ),
    ),
    { initialValue: [] as AiReview[] },
  );

  /** Bumped after adding or removing someone, to refetch the team. */
  private readonly stakeholdersRefresh = signal(0);

  /** True while an add or remove is in flight. */
  protected readonly stakeholdersBusy = signal(false);

  /**
   * The round's Project Team. Also the source of the comment composer's Notify
   * options, so it is one fetch feeding both.
   *
   * The API reconciles this against the round's feedback and comment history, so it
   * covers everyone who has taken part even on rounds created before stakeholders
   * were tracked.
   */
  private readonly stakeholdersResult = toSignal(
    toObservable(
      computed(() => ({ roundId: this.id(), refresh: this.stakeholdersRefresh() })),
    ).pipe(
      switchMap(({ roundId }) =>
        roundId
          ? this.feedbackRounds.listStakeholders(roundId).pipe(
              map((stakeholders) => ({ loading: false, stakeholders })),
              // A failed fetch shows an empty team rather than breaking the page.
              catchError(() => of({ loading: false, stakeholders: [] as FeedbackRoundUser[] })),
              startWith({ loading: true, stakeholders: [] as FeedbackRoundUser[] }),
            )
          : of({ loading: false, stakeholders: [] as FeedbackRoundUser[] }),
      ),
    ),
    { initialValue: { loading: false, stakeholders: [] as FeedbackRoundUser[] } },
  );

  protected readonly stakeholders = computed(() => this.stakeholdersResult().stakeholders);

  protected readonly stakeholdersLoading = computed(() => this.stakeholdersResult().loading);

  protected readonly statuses = Object.values(FeedbackRoundStatus);

  /**
   * AI assistant prompts for this round. All three reviews share the URL and
   * mockup questions (URL options come from the round's first pages; mockup options
   * from `mockupUrls`, with the chosen page's own mockup floated to the top);
   * the guided and advanced reviews add free-text steering questions.
   */
  protected readonly aiPrompts = computed<AiPrompt[]>(() => {
    const round = this.round();
    if (!round) {
      return [];
    }
    const pages = this.aiPages();
    const mockupUrls = round.mockupUrls ?? [];
    const pageOptions = pages
      .map((page) => page.fullUrl ?? page.pageUrl)
      .filter((url): url is string => !!url);

    const urlQuestion: AiPromptQuestion = {
      key: 'url',
      text: 'What URL would you like to review?',
      // A dropdown rather than chips: rounds can have many pages with long URLs.
      display: 'dropdown',
      options: pageOptions,
      hint: 'You may also simply provide a URL below.',
    };

    const mockupQuestion: AiPromptQuestion = {
      key: 'mockup',
      text: 'What mockup should be used?',
      // A custom dropdown: each row previews the mockup thumbnail and shows only
      // the filename, while the saved answer is the full mockup URL.
      display: 'customdropdown',
      options: (answers) => {
        const selectedPage = pages.find((page) => (page.fullUrl ?? page.pageUrl) === answers['url']);
        const pageMockup = selectedPage?.mockupUrl;
        // First option is the chosen page's own mockup, when it has one.
        const ordered = pageMockup
          ? [pageMockup, ...mockupUrls.filter((mockup) => mockup !== pageMockup)]
          : [...mockupUrls];
        return ordered.map((url) => ({ value: url, label: this.fileName(url), thumbnail: url }));
      },
    };

    return [
      {
        id: 'basic',
        icon: 'auto_awesome',
        color: '#ec1c7a',
        title: 'Basic AI Review',
        description:
          'Compares the mockup to the live page and reports the differences as new feedback. Existing feedback isn’t considered.',
        questions: [urlQuestion, mockupQuestion],
      },
      {
        id: 'guided',
        icon: 'explore',
        color: '#1bb6c1',
        title: 'Guided AI Review',
        description:
          'Takes browser steps you describe (e.g. open a menu), then compares the mockup to that view of the live page and reports the differences as new feedback. Existing feedback isn’t considered.',
        questions: [
          urlQuestion,
          mockupQuestion,
          {
            key: 'computerInstructions',
            text: 'Any steps the AI should take in the browser before reviewing? (e.g. log in, open a menu)',
            options: [],
          },
          {
            key: 'feedbackDirection',
            text: 'What should the feedback focus on?',
            options: [],
          },
        ],
      },
      {
        id: 'advanced',
        icon: 'tune',
        color: '#7c3aed',
        title: 'Advanced AI Review',
        // Mirrors AIAgentService::advancedAiReview: it re-checks the page's feedback in
        // Needs Internal/Client Review (resolving each to Completed or Revise) and adds
        // any new differences from the mockup.
        description:
          'Checks whether feedback awaiting review has been implemented, marking each Completed or sending it back to Revise with a note, and reports any new issues. Steer it with a custom prompt.',
        questions: [
          urlQuestion,
          mockupQuestion,
          {
            key: 'prompt',
            text: 'Add a custom prompt to guide the review.',
            options: [],
          },
        ],
      },
      {
        id: 'linkCheck',
        icon: 'link_off',
        color: '#e8710a',
        title: 'Link Checker',
        description:
          'Checks a page, or every page in the site’s sitemap, for broken links and images (404s) and adds each as feedback. Server errors (5xx) are summed up in one item.',
        questions: [
          {
            key: 'url',
            text: 'Which page should be checked?',
            display: 'dropdown',
            options: [{ value: ALL_SITEMAP_PAGES, label: 'All pages (from the sitemap)' }, ...pageOptions],
            hint: SITEMAP_URL_HINT,
          },
          // One of these two is asked, depending on how the sitemap was picked.
          {
            key: 'exclusions',
            text: ALL_PAGES_EXCLUSIONS_QUESTION,
            options: ['None'],
            when: (answers) => answers['url'] === ALL_SITEMAP_PAGES,
          },
          {
            key: 'exclusions',
            text: TYPED_SITEMAP_EXCLUSIONS_QUESTION,
            options: ['None'],
            when: isTypedSitemap,
          },
        ],
      },
      {
        id: 'copyCheck',
        icon: 'spellcheck',
        color: '#0b8043',
        title: 'Spelling & Grammar',
        description:
          'Proofreads the user-facing copy of a page, a sitemap, or the whole site (up to 500 pages) in the English you choose, and adds each spelling, grammar or punctuation issue as feedback. Header and footer copy is checked once.',
        questions: [
          {
            key: 'url',
            text: 'Which page should be checked?',
            display: 'dropdown',
            options: [
              { value: ALL_SITEMAP_PAGES, label: 'All pages (from the sitemap, up to 500)' },
              { value: SPECIFIC_SITEMAP, label: 'A specific sitemap…' },
              ...pageOptions,
            ],
            hint: SITEMAP_URL_HINT,
          },
          {
            key: 'sitemapUrl',
            text: 'What’s the sitemap URL? (e.g. https://example.com/post-sitemap.xml)',
            options: [],
            when: (answers) => answers['url'] === SPECIFIC_SITEMAP,
          },
          // One of these two is asked, depending on how the sitemap was picked.
          {
            key: 'exclusions',
            text: ALL_PAGES_EXCLUSIONS_QUESTION,
            options: ['None'],
            when: (answers) => answers['url'] === ALL_SITEMAP_PAGES,
          },
          {
            key: 'exclusions',
            text: TYPED_SITEMAP_EXCLUSIONS_QUESTION,
            options: ['None'],
            when: isTypedSitemap,
          },
          {
            key: 'englishVariant',
            text: 'Which English should the copy follow? Pick one, or describe it (e.g. "UK, but we use -ize").',
            options: ['US English', 'UK English', 'Australian English', 'Canadian English'],
          },
        ],
      },
      {
        id: 'audit',
        icon: 'fact_check',
        color: '#5b3cc4',
        title: 'Feedback Audit Report',
        description:
          'Classifies every piece of feedback as a Brafton error, client data, a client change request or non-actionable — using the mockups, screenshots, comments, revise loops and the project\'s copy doc / DRD assets (plus any you paste) — and writes a Google Doc on where Brafton did well and where to improve.',
        questions: [
          {
            key: 'copyDocuments',
            text: 'The project\'s Copy doc assets are read automatically. Paste any more Google Doc or Sheet links for the approved copy, or choose None.',
            options: [NO_DOCUMENTS],
            hint: 'Several links can be pasted at once, separated by spaces or new lines.',
          },
          {
            key: 'drdDocument',
            text: 'The project\'s DRD assets are read automatically. Paste another Google Doc or Sheet link for the DRD (design / copy / development requirements), or choose None.',
            options: [NO_DOCUMENTS],
          },
        ],
      },
    ];
  });

  // Bumped to force a refetch of the current feedback page (e.g. after a create).
  private readonly feedbackRefresh = signal(0);

  // The last feedback view (params key + item ids) seen, so a poll can detect
  // genuinely new feedback and toast about it. Excludes the manual refresh.
  private previousFeedbackView: { key: string; ids: Set<string> } | null = null;

  // Set when the user creates feedback themselves, so the "new feedback" toast is
  // skipped for their own addition (they already get a "Feedback created" toast).
  private suppressNextNewFeedbackToast = false;

  /** True while a page of feedback is being fetched for new params (not the background poll). */
  protected readonly feedbackLoading = signal(true);

  // The current page of feedback, from the paginated feedback endpoint.
  private readonly feedbackResult = toSignal(
    toObservable(
      computed(() => ({
        roundId: this.id(),
        page: this.feedbackPage(),
        sort: this.sort()
          .map((spec) => `${spec.col}:${spec.dir}`)
          .join(','),
        filters: this.filters(),
        refresh: this.feedbackRefresh(),
      })),
    ).pipe(
      // Fetch immediately on any parameter change, then re-poll with those same
      // params every 60s. A parameter change restarts the poll timer.
      switchMap((query) =>
        merge(
          of({ query, isPoll: false }),
          timer(FEEDBACK_POLL_INTERVAL_MS, FEEDBACK_POLL_INTERVAL_MS).pipe(map(() => ({ query, isPoll: true }))),
        ),
      ),
      // The table shows its loading state for a parameter change, not for the
      // silent background poll.
      tap(({ isPoll }) => {
        if (!isPoll) {
          this.feedbackLoading.set(true);
        }
      }),
      // Keep the query paired with its page so new-feedback detection compares
      // like-for-like (the params that produced the page).
      switchMap(({ query }) =>
        (query.roundId
          ? this.feedback
              .listFeedback({
                feedbackRoundId: query.roundId,
                page: query.page,
                sort: query.sort,
                url: query.filters.url,
                category: query.filters.category as FeedbackCategory | undefined,
                screen: query.filters.screen as ScreenType | undefined,
                status: toStatusFilter(query.filters.status),
                type: query.filters.type as FeedbackType | undefined,
                score: query.filters.score as FeedbackScoreBand | undefined,
                user: query.filters.user,
                ...toCreatedRange(query.filters),
              })
              .pipe(catchError(() => of(EMPTY_FEEDBACK_PAGE)))
          : of(EMPTY_FEEDBACK_PAGE)
        ).pipe(
          // Cleared before the distinct check below, which may swallow an unchanged page.
          tap(() => this.feedbackLoading.set(false)),
          map((page) => ({ query, page })),
        ),
      ),
      // Only surface a new page when it actually differs, so a poll that finds
      // nothing new leaves the table (and any optimistic edits) untouched.
      distinctUntilChanged(
        (previous, current) => feedbackSignature(previous.page) === feedbackSignature(current.page),
      ),
      // Toast when a poll brings in feedback that wasn't on the current view before.
      tap((emission) => this.notifyIfNewFeedback(emission)),
      map((emission) => emission.page),
    ),
    { initialValue: EMPTY_FEEDBACK_PAGE },
  );

  // Locally-editable copy of the current page, seeded from the fetch, so a status
  // change in the drawer reflects in the table optimistically.
  protected readonly feedbackData = linkedSignal(() => this.feedbackResult());

  /**
   * The table rows ticked for a bulk status change. Cleared whenever a page of
   * feedback loads (paging, filtering, sorting): bulk select works on the rows in view.
   */
  protected readonly selectedFeedbackIds = linkedSignal<Paginated<Feedback>, string[]>({
    source: this.feedbackResult,
    computation: () => [],
  });

  /**
   * Unread comment counts for the signed-in user across this round, keyed by feedback
   * id. Keyed off the same refresh signal as the feedback page, so the 60s poll keeps
   * the badges current, and off commentsRefresh so reading a thread clears its badge.
   */
  private readonly unreadComments = toSignal(
    toObservable(
      computed(() => ({
        roundId: this.id(),
        email: this.currentUser.email(),
        refresh: this.feedbackRefresh() + this.commentsRefresh(),
      })),
    ).pipe(
      switchMap(({ roundId, email }) =>
        roundId && email
          ? this.comments
              .listUnreadCounts({ feedbackRoundId: roundId, email })
              // Badges are a nicety; a failed fetch must not disturb the table.
              .pipe(catchError(() => of({} as UnreadCommentCounts)))
          : of({} as UnreadCommentCounts),
      ),
    ),
    { initialValue: {} as UnreadCommentCounts },
  );

  protected readonly feedbackRows = computed<FeedbackRow[]>(() =>
    this.feedbackData().results.map((item) => ({
      id: item.id,
      category: item.category ?? '',
      element: item.element ?? '',
      url: item.url ?? '',
      // Stored as a 0–1 fraction; the table shows it on a 0–10 scale.
      score: item.score == null ? null : item.score * 10,
      screen: this.screenFor(item.meta),
      status: item.status ?? '',
      type: item.type ?? '',
      created: item.createdAt ?? '',
      unreadComments: this.unreadComments()[item.id] ?? 0,
    })),
  );

  protected readonly feedbackTotal = computed(() => this.feedbackData().total);
  protected readonly feedbackPerPage = computed(() => this.feedbackData().perPage);
  protected readonly feedbackCurrentPage = computed(() => this.feedbackData().page);

  /** Status values offered in the feedback detail drawer's status chip. */
  protected readonly feedbackStatuses = Object.values(FeedbackStatus);

  /** The project avatar, keyed by round id so it shows before the round has loaded. */
  protected avatarFor(roundId: string): ProjectIcon {
    return pickProjectIcon(roundId);
  }

  /** The feedback shown in the detail drawer; null when the drawer is closed. */
  protected readonly selectedFeedback = signal<FeedbackDetail | null>(null);

  constructor() {
    // Deep-link: when arriving with `?feedback={id}` (the widget's "open in app"
    // link), fetch that feedback by id and open its detail drawer.
    toObservable(this.feedbackParam)
      .pipe(
        switchMap((feedbackId) =>
          feedbackId
            ? this.feedback.getFeedback(feedbackId).pipe(catchError(() => of(null)))
            : of(null),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((item) => {
        if (item) {
          this.selectedFeedback.set(this.toFeedbackDetail(item));
          this.markCommentsRead(item.id);
        }
      });

    // Know up front whether Google is connected (the audit and the Google exports
    // need it) and have Google's script ready, so a needed consent popup can open
    // right inside the user's click instead of the API rejecting the request.
    effect(() => {
      if (this.currentUser.email()) {
        untracked(() => {
          this.googleAuthCode.preload();
          this.refreshGoogleConnection();
        });
      }
    });
  }

  /** Bumped after posting, to refetch the thread. */
  private readonly commentsRefresh = signal(0);

  // The comment thread for the open drawer. Keyed on the selected feedback, so
  // closing the drawer empties it and opening another item refetches.
  private readonly commentsResult = toSignal(
    toObservable(
      computed(() => ({
        feedbackId: this.selectedFeedback()?.id ?? '',
        refresh: this.commentsRefresh(),
      })),
    ).pipe(
      switchMap(({ feedbackId }) =>
        feedbackId
          ? this.comments.listComments(feedbackId).pipe(
              map((comments) => ({ loading: false, comments })),
              // A failed fetch shows an empty thread rather than breaking the drawer.
              catchError(() => of({ loading: false, comments: [] as Comment[] })),
              startWith({ loading: true, comments: [] as Comment[] }),
            )
          : of({ loading: false, comments: [] as Comment[] }),
      ),
    ),
    { initialValue: { loading: false, comments: [] as Comment[] } },
  );

  /** The thread in the drawer's presentational shape. */
  protected readonly commentThread = computed(() =>
    this.commentsResult().comments.map((comment) => this.toCommentThreadEntry(comment)),
  );

  protected readonly commentsLoading = computed(() => this.commentsResult().loading);

  /** True while a comment is being sent. */
  protected readonly commentPosting = signal(false);

  /**
   * Who can be notified about a comment: the round's Project Team.
   *
   * Built from the stakeholder list rather than from the feedback-author filter,
   * because the people worth notifying include everyone who has COMMENTED as well as
   * everyone who has left feedback — and the author filter, by its nature, only knows
   * about the latter. It also means the round's creator is offered before any feedback
   * exists at all.
   */
  protected readonly commentRecipientOptions = computed<CommentRecipientOption[]>(() =>
    this.stakeholders()
      .map((stakeholder) => ({
        email: (stakeholder.email ?? '').trim(),
        label: (stakeholder.name ?? '').trim() || (stakeholder.email ?? '').trim(),
      }))
      .filter((option) => option.email.includes('@')),
  );

  /**
   * The open item's own author: badged in that picker, and the default recipient until
   * somebody has commented on the item.
   */
  protected readonly commentRecipientAuthorEmail = computed(
    () => this.selectedFeedback()?.createdBy?.email ?? null,
  );

  /** The signed-in user, so the picker never defaults to notifying them. */
  protected readonly currentUserEmail = computed(() => this.currentUser.email());

  /**
   * Profile photos for the Project Team cards, by email. Only the signed-in user's
   * (from auth) is known; everyone else shows their initials.
   */
  protected readonly teamPictureUrls = computed<Record<string, string>>(() => {
    const email = this.currentUser.email();
    const pictureUrl = this.currentUser.pictureUrl();
    return email && pictureUrl ? { [email]: pictureUrl } : {};
  });

  /** Adds someone to the round's team by hand from the panel. */
  protected onAddStakeholder(stakeholder: NewStakeholder): void {
    const roundId = this.id();
    if (!roundId) {
      return;
    }

    this.stakeholdersBusy.set(true);
    this.feedbackRounds
      .addStakeholder(roundId, {
        email: stakeholder.email,
        // Sent as null rather than '' so the API applies its own fallback (the
        // address) instead of storing an empty name.
        name: stakeholder.name || null,
        role: stakeholder.role,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.stakeholdersBusy.set(false);
          this.stakeholdersRefresh.update((count) => count + 1);
        },
        error: () => {
          this.stakeholdersBusy.set(false);
          this.snackBar.open('Could not add that person to the project team.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }

  /** Saves a role picked in the panel. Anyone may change anyone's role. */
  protected onStakeholderRoleChange(change: StakeholderRoleChange): void {
    const roundId = this.id();
    if (!roundId) {
      return;
    }

    this.stakeholdersBusy.set(true);
    this.feedbackRounds
      .updateStakeholderRole(roundId, { email: change.email, role: change.role })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.stakeholdersBusy.set(false);
          this.stakeholdersRefresh.update((count) => count + 1);
        },
        error: () => {
          this.stakeholdersBusy.set(false);
          this.snackBar.open('Could not change that role.', 'Dismiss', { duration: 5000 });
        },
      });
  }

  /** Removes someone from the round's team. */
  protected onRemoveStakeholder(email: string): void {
    const roundId = this.id();
    if (!roundId) {
      return;
    }

    this.stakeholdersBusy.set(true);
    this.feedbackRounds
      .removeStakeholder(roundId, email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.stakeholdersBusy.set(false);
          this.stakeholdersRefresh.update((count) => count + 1);
        },
        error: () => {
          this.stakeholdersBusy.set(false);
          this.snackBar.open('Could not remove that person from the project team.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }

  /** Whether the new-feedback drawer is open. */
  protected readonly newFeedbackOpen = signal(false);

  /** True while a new feedback (and its screenshot) is being uploaded/created. */
  protected readonly creatingFeedback = signal(false);

  protected onOpenFeedback(row: FeedbackRow): void {
    const item = this.feedbackData().results.find((feedback) => feedback.id === row.id);
    if (item) {
      this.selectedFeedback.set(this.toFeedbackDetail(item));
      this.markCommentsRead(item.id);
    }
  }

  /** Persists a status change made in the detail drawer. */
  protected onFeedbackStatusChange(nextStatus: string): void {
    const selected = this.selectedFeedback();
    if (selected) {
      this.changeFeedbackStatus(selected.id, selected.status, nextStatus);
    }
  }

  /**
   * Records that the signed-in user has read this item's thread, which clears its
   * unread badge. Fire-and-forget: a failure only leaves the badge in place.
   */
  private markCommentsRead(feedbackId: string): void {
    const email = this.currentUser.email();
    if (!email) {
      return;
    }

    this.comments
      .markCommentsRead(feedbackId, { email })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.commentsRefresh.update((count) => count + 1),
        error: () => undefined,
      });
  }

  /**
   * Posts a comment on the open feedback item, then refetches the thread so the
   * stored record (server id, timestamp, resolved author side) is what gets shown.
   */
  protected onPostComment(newComment: NewComment): void {
    const selected = this.selectedFeedback();
    if (!selected) {
      return;
    }

    const author = this.currentUser.author();
    if (!author.name || !author.email) {
      // The API requires both to attribute the comment, and inventing either would
      // put a wrong name on the record.
      this.snackBar.open('Your profile has no name or email, so comments cannot be attributed.', 'Dismiss', {
        duration: 6000,
      });
      return;
    }

    this.commentPosting.set(true);
    this.comments
      .createComment(selected.id, {
        comment: newComment.comment,
        createdBy: { name: author.name, email: author.email },
        isPrivate: newComment.isPrivate,
        recipients: newComment.recipients,
        // False when nobody was picked: a note (e.g. one left for yourself), emailed to no one.
        notify: newComment.notify,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.commentPosting.set(false);
          this.commentsRefresh.update((refreshCount) => refreshCount + 1);
          // Commenting makes the author a stakeholder server-side, so pull the team
          // again to keep the panel and the Notify options in step.
          this.stakeholdersRefresh.update((count) => count + 1);
        },
        error: () => {
          this.commentPosting.set(false);
          this.snackBar.open('Could not post the comment.', 'Dismiss', { duration: 5000 });
        },
      });
  }

  /** Maps a stored comment onto the thread component's shape. */
  private toCommentThreadEntry(comment: Comment): CommentThreadEntry {
    return {
      id: comment.id,
      author: comment.createdBy?.name,
      // Not rendered; it is what lets the composer default the Notify picker to
      // whoever commented last.
      authorEmail: comment.createdBy?.email ?? null,
      role: comment.createdBy?.role ?? null,
      side: comment.authorType === CommentAuthorType.Client ? 'client' : 'team',
      text: comment.comment ?? '',
      created: comment.createdAt,
      isPrivate: comment.isPrivate,
    };
  }

  /** Persists a status change made inline on a table row. */
  protected onRowStatusChange(change: FeedbackRowStatusChange): void {
    this.changeFeedbackStatus(change.row.id, change.row.status, change.status);
  }

  /**
   * Persists a feedback status change, updating the table row and (if open) the
   * detail drawer optimistically, reverting both if the request fails.
   */
  private changeFeedbackStatus(
    feedbackId: string,
    currentStatus: string | undefined,
    nextStatus: string,
    disregardReason?: DisregardReason,
  ): void {
    const previousStatus = currentStatus as FeedbackStatus | undefined;
    const next = nextStatus as FeedbackStatus;
    if (next === previousStatus) {
      return;
    }
    // Disregarding needs a reason first: ask, and carry on from onDisregardConfirmed.
    if (next === FeedbackStatus.Disregard && !disregardReason) {
      const feedbackItem = this.feedbackData().results.find((feedback) => feedback.id === feedbackId);
      this.disregardPending.set({ feedbackId, currentStatus, element: feedbackItem?.element ?? null });
      return;
    }

    this.patchFeedbackStatus(feedbackId, next);
    this.feedback
      // The signed-in user is recorded as who made the change (feeds the audit report).
      .updateFeedbackStatus(feedbackId, next, this.currentUser.author())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // The round's progress (completed/disregarded over all non-stub
        // feedback) is computed server-side, so refetch it once the change has
        // persisted to keep the progress bar in step with the status change.
        next: () => {
          this.refreshRoundProgress();
          if (disregardReason) {
            this.recordDisregardReason(feedbackId, disregardReason);
          }
        },
        error: () => {
          if (previousStatus) {
            this.patchFeedbackStatus(feedbackId, previousStatus);
          }
          this.snackBar.open('Could not update the feedback status. Reverted.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }

  /** An item waiting on a reason before it's disregarded (the reason dialog is open). */
  protected readonly disregardPending = signal<{
    feedbackId: string;
    currentStatus: string | undefined;
    element: string | null;
  } | null>(null);

  protected onDisregardConfirmed(disregardReason: DisregardReason): void {
    const pending = this.disregardPending();
    this.disregardPending.set(null);
    if (pending) {
      this.changeFeedbackStatus(pending.feedbackId, pending.currentStatus, FeedbackStatus.Disregard, disregardReason);
    }
  }

  /** Backing out leaves the status as it was (it was never changed). */
  protected onDisregardCancelled(): void {
    this.disregardPending.set(null);
    this.bulkDisregardPending.set(null);
  }

  /** Selected rows waiting on one reason before they're all disregarded. */
  protected readonly bulkDisregardPending = signal<FeedbackRow[] | null>(null);

  /** The reason dialog's subtitle: the item, or how many are being disregarded together. */
  protected readonly disregardItemLabel = computed(() => {
    const bulkRows = this.bulkDisregardPending();
    if (bulkRows) {
      return `${bulkRows.length} selected item${bulkRows.length === 1 ? '' : 's'}`;
    }
    return this.disregardPending()?.element ?? null;
  });

  protected onDisregardReasonConfirmed(disregardReason: DisregardReason): void {
    const bulkRows = this.bulkDisregardPending();
    if (bulkRows) {
      this.bulkDisregardPending.set(null);
      this.changeFeedbackStatuses(bulkRows, FeedbackStatus.Disregard, disregardReason);
      return;
    }
    this.onDisregardConfirmed(disregardReason);
  }

  /** A status chosen from the table's bulk bar for every selected row. */
  protected onBulkStatusChange(bulkChange: FeedbackBulkStatusChange): void {
    this.changeFeedbackStatuses(bulkChange.rows, bulkChange.status);
  }

  /**
   * Sets one status on several rows in a single request, updating them optimistically
   * and reverting each if it fails. Disregarding asks for one reason first, recorded
   * on every item. The selection clears once the change is saved.
   */
  private changeFeedbackStatuses(rows: FeedbackRow[], nextStatus: string, disregardReason?: DisregardReason): void {
    const roundId = this.id();
    const next = nextStatus as FeedbackStatus;
    const rowsToChange = rows.filter((row) => row.status !== next);
    if (!roundId || !rowsToChange.length) {
      this.selectedFeedbackIds.set([]);
      return;
    }
    if (next === FeedbackStatus.Disregard && !disregardReason) {
      this.bulkDisregardPending.set(rowsToChange);
      return;
    }

    for (const row of rowsToChange) {
      this.patchFeedbackStatus(row.id, next);
    }
    this.feedback
      .bulkUpdateFeedbackStatus(
        roundId,
        rowsToChange.map((row) => row.id),
        next,
        this.currentUser.author(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updateResult) => {
          this.selectedFeedbackIds.set([]);
          this.refreshRoundProgress();
          if (disregardReason) {
            for (const row of rowsToChange) {
              this.recordDisregardReason(row.id, disregardReason);
            }
          }
          const statusLabel = this.humanizeFilterValue(next);
          this.snackBar.open(
            `Set ${updateResult.updated} item${updateResult.updated === 1 ? '' : 's'} to ${statusLabel}.`,
            'Dismiss',
            { duration: 4000 },
          );
        },
        error: () => {
          for (const row of rowsToChange) {
            this.patchFeedbackStatus(row.id, row.status as FeedbackStatus);
          }
          this.snackBar.open('Could not update the selected feedback. Reverted.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }

  /**
   * Saves why an item was disregarded as a private comment, emailing nobody — it
   * informs the feedback audit (and, later, the AI reviewer) rather than starting
   * a conversation.
   */
  private recordDisregardReason(feedbackId: string, disregardReason: DisregardReason): void {
    const author = this.currentUser.author();
    if (!author.name || !author.email) {
      // The API needs both to attribute the comment; inventing either would be wrong.
      this.snackBar.open('The item was disregarded, but your profile has no name or email to record the reason under.', 'Dismiss', {
        duration: 6000,
      });
      return;
    }
    this.comments
      .createComment(feedbackId, {
        comment: disregardReasonComment(disregardReason),
        createdBy: { name: author.name, email: author.email },
        isPrivate: true,
        notify: false,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.selectedFeedback()?.id === feedbackId) {
            this.commentsRefresh.update((refreshCount) => refreshCount + 1);
          }
        },
        error: () =>
          this.snackBar.open('The item was disregarded, but the reason could not be saved as a comment.', 'Dismiss', {
            duration: 6000,
          }),
      });
  }

  /**
   * Refetches the round's server-computed `progress` and patches just that field
   * onto the local round, so the progress bar tracks feedback status changes.
   *
   * Progress is deliberately not derived client-side: the feedback table is
   * paginated and filtered, whereas the server computes progress across the
   * round's entire non-stub feedback set. Patching only `progress` preserves the
   * other optimistic edits held on the local round (status, pages, mockups).
   */
  private refreshRoundProgress(): void {
    const roundId = this.id();
    if (!roundId) {
      return;
    }
    this.feedbackRounds
      .getFeedbackRound(roundId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (freshRound) =>
          this.round.update((current) =>
            current ? { ...current, progress: freshRound.progress } : current,
          ),
        // A failed progress refresh is non-critical; leave the current value.
      });
  }

  /**
   * Toasts when a poll surfaces feedback that wasn't on the current view before.
   * Compares the new page against the previously seen one *for the same params*,
   * so it never fires on the initial load or a filter/sort/page change. Skips the
   * toast for feedback the user just created themselves.
   */
  private notifyIfNewFeedback(emission: {
    query: { roundId: string; page: number; sort: string; filters: FeedbackFilters };
    page: Paginated<Feedback>;
  }): void {
    const { query, page } = emission;
    const key = `${query.roundId}|${query.page}|${query.sort}|${JSON.stringify(query.filters)}`;
    const ids = page.results.map((item) => item.id);
    const previous = this.previousFeedbackView;
    this.previousFeedbackView = { key, ids: new Set(ids) };

    // Initial load or a parameter change: set the baseline, don't notify.
    if (!previous || previous.key !== key) {
      return;
    }
    const newCount = ids.filter((id) => !previous.ids.has(id)).length;
    if (newCount === 0) {
      return;
    }
    if (this.suppressNextNewFeedbackToast) {
      this.suppressNextNewFeedbackToast = false;
      return;
    }
    this.snackBar.open(
      `${newCount} new feedback item${newCount === 1 ? '' : 's'} came in.`,
      'Dismiss',
      { duration: 6000 },
    );
  }

  /** Patches a feedback item's status in both the drawer and the table page. */
  private patchFeedbackStatus(feedbackId: string, status: FeedbackStatus): void {
    this.selectedFeedback.update((current) =>
      current && current.id === feedbackId ? { ...current, status } : current,
    );
    this.feedbackData.update((page) => ({
      ...page,
      results: page.results.map((item) =>
        item.id === feedbackId ? { ...item, status } : item,
      ),
    }));
  }

  /** Opens the new-feedback drawer. */
  protected onNewFeedback(): void {
    this.newFeedbackOpen.set(true);
  }

  /** Whether the export-feedback dialog is open. */
  protected readonly exportOpen = signal(false);

  /** Human labels for the active feedback filters, shown as chips in the dialog. */
  private readonly filterChipLabels: Record<keyof FeedbackFilters, string> = {
    url: 'URL',
    category: 'Category',
    screen: 'Screen',
    status: 'Status',
    type: 'Type',
    score: 'Score',
    user: 'User',
    createdFrom: 'Created from',
    createdTo: 'Created to',
  };

  /** The active filters projected into the export dialog's chip shape. */
  protected readonly exportFilterChips = computed<ExportFilterChip[]>(() => {
    const active = this.filters();
    const chips: ExportFilterChip[] = [];
    for (const key of Object.keys(this.filterChipLabels) as (keyof FeedbackFilters)[]) {
      const value = active[key];
      if (value) {
        // The user filter's value is an author email (or the 'ai' sentinel), so
        // show its option label rather than title-casing the raw value.
        // Dates are shown as written (humanizing would turn "2026-07-01" into "2026 07 01").
        const isDateFilter = key === 'createdFrom' || key === 'createdTo';
        const display =
          key === 'user' ? this.userFilterLabel(value) : isDateFilter ? value : this.humanizeFilterValue(value);
        chips.push({ key: this.filterChipLabels[key], value: display });
      }
    }
    return chips;
  });

  /** The display label for a selected user-filter value, falling back to the value. */
  private userFilterLabel(value: string): string {
    return this.feedbackUserOptions().find((option) => option.value === value)?.label ?? value;
  }

  /** Title-cases an enum-style filter value (e.g. needs_internal_review). */
  private humanizeFilterValue(value: string): string {
    return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
  }

  /** Opens the export-feedback dialog. */
  protected onExportFeedback(): void {
    this.exportOpen.set(true);
    // Ready the Google export: load Google's sign-in script now (so choosing Google
    // Sheets can open the consent popup inside the click, unblocked) and learn
    // whether this user has connected Google yet.
    this.googleAuthCode.preload();
    this.refreshGoogleConnection();
  }

  /**
   * Whether the signed-in user has connected Google — checked when the page opens
   * and again when the export dialog opens; null while unknown (not checked yet, or
   * the check failed).
   */
  private readonly googleConnected = signal<boolean | null>(null);

  /** The Google permissions the user granted; null while unknown. */
  private readonly googleGrantedScopes = signal<string[] | null>(null);

  private refreshGoogleConnection(): void {
    const email = this.currentUser.email();
    if (!email) {
      return;
    }
    this.googleConnections
      .getConnection(email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (googleConnection) => this.rememberGoogleConnection(googleConnection),
        error: () => {
          this.googleConnected.set(null);
          this.googleGrantedScopes.set(null);
        },
      });
  }

  private rememberGoogleConnection(googleConnection: GoogleConnection): void {
    this.googleConnected.set(googleConnection.connected);
    this.googleGrantedScopes.set(googleConnection.scopes ?? []);
  }

  /**
   * Whether an audit can start without first connecting Google: false when the
   * user isn't connected, or there are documents to read (pasted links, or the
   * round's copy doc / DRD assets) but Docs and Sheets read access isn't granted.
   * True when unknown — the API still checks and the error path offers Connect Google.
   */
  private googleReadyForAudit(hasPastedDocuments: boolean): boolean {
    if (this.googleConnected() === false) {
      return false;
    }
    const roundHasDocumentAssets = (this.round()?.assets ?? []).some(
      (roundAsset) => roundAsset.assetType === RoundAssetType.Copy || roundAsset.assetType === RoundAssetType.Drd,
    );
    const grantedScopes = this.googleGrantedScopes();
    const hasEveryReadScope =
      grantedScopes === null ||
      (grantedScopes.includes(GOOGLE_DOCUMENTS_READ_SCOPE) && grantedScopes.includes(GOOGLE_SPREADSHEETS_READ_SCOPE));
    return !((hasPastedDocuments || roundHasDocumentAssets) && !hasEveryReadScope);
  }

  /** Display labels for the export formats, used in the placeholder toast. */
  private readonly exportFormatLabels: Record<ExportFormat, string> = {
    gsheet: 'Google Sheets',
    csv: 'CSV',
    gdoc: 'Google Docs',
    pdf: 'PDF',
  };

  /** True while an export is gathering feedback / building the file or sheet. */
  protected readonly exporting = signal(false);

  /**
   * Handles a chosen export format. CSV is built client-side; Google Sheets and
   * Docs are built by the API in the user's Drive. The other formats are wired up
   * one at a time and show a placeholder toast until then.
   */
  protected onExportFormat(format: ExportFormat): void {
    this.exportOpen.set(false);
    if (format === 'csv') {
      this.exportCsv();
      return;
    }
    if (format === 'gsheet' || format === 'gdoc') {
      this.exportToGoogle(format);
      return;
    }
    this.snackBar.open(
      `${this.exportFormatLabels[format]} export isn’t implemented yet.`,
      'Dismiss',
      { duration: 4000 },
    );
  }

  /**
   * Exports every feedback item matching the current filters, in the current sort
   * order, to a new Google Sheet or Doc in the user's Drive. The API gathers the
   * feedback and creates the file; a user who hasn't connected Google is sent
   * through Google's consent popup first, opened right here inside the click.
   */
  private exportToGoogle(exportKind: GoogleExportKind): void {
    const roundId = this.id();
    const email = this.currentUser.email();
    if (!roundId || this.exporting()) {
      return;
    }
    if (!email) {
      this.snackBar.open(`Sign in again to export to ${this.exportFormatLabels[exportKind]}.`, 'Dismiss', {
        duration: 5000,
      });
      return;
    }
    if (this.googleConnected() === true) {
      this.runGoogleExport(exportKind, roundId, email);
      return;
    }
    this.connectGoogleThenExport(exportKind, roundId, email);
  }

  /**
   * Runs Google's consent popup, hands the one-time code to the API (which keeps
   * the Google grant server-side), then exports. Must start inside a user gesture:
   * the popup is requested synchronously, before anything asynchronous.
   */
  private connectGoogleThenExport(exportKind: GoogleExportKind, roundId: string, email: string): void {
    const authorizationCodeRequest = this.googleAuthCode.requestCode(email);
    this.exporting.set(true);
    authorizationCodeRequest
      .then((authorizationCode) =>
        firstValueFrom(this.googleConnections.connect({ email, code: authorizationCode })),
      )
      .then((googleConnection) => {
        this.rememberGoogleConnection(googleConnection);
        this.exporting.set(false);
        this.runGoogleExport(exportKind, roundId, email);
      })
      .catch((connectError: unknown) => {
        this.exporting.set(false);
        this.snackBar.open(this.googleExportErrorMessage(exportKind, connectError), 'Dismiss', {
          duration: 6000,
        });
      });
  }

  private runGoogleExport(exportKind: GoogleExportKind, roundId: string, email: string): void {
    this.exporting.set(true);
    const exportRequest = { email, ...this.currentFeedbackQueryFilters() };
    const exportLabel = this.exportFormatLabels[exportKind];
    this.snackBar.open(
      exportKind === 'gsheet' ? 'Creating your Google Sheet…' : 'Creating your Google Doc…',
      undefined,
      { duration: 3000 },
    );

    const createdFile$: Observable<GoogleExportResult> =
      exportKind === 'gsheet'
        ? this.googleExport.exportGoogleSheet(roundId, exportRequest).pipe(
            map((spreadsheet) => ({ url: spreadsheet.url, itemCount: spreadsheet.rowCount, note: '' })),
          )
        : this.googleExport.exportGoogleDoc(roundId, exportRequest).pipe(
            map((document) => ({
              url: document.url,
              itemCount: document.itemCount,
              note: document.imagesLinked
                ? ` ${document.imagesLinked} screenshot${document.imagesLinked === 1 ? '' : 's'} could not be embedded and ${document.imagesLinked === 1 ? 'is' : 'are'} linked instead.`
                : '',
            })),
          );

    createdFile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (createdFile) => {
          this.exporting.set(false);
          // Opened from the toast action: a tab opened after the async export would
          // be popup-blocked, but the action click is a fresh user gesture.
          this.snackBar
            .open(
              `Exported ${createdFile.itemCount} feedback item${createdFile.itemCount === 1 ? '' : 's'} to ${exportLabel}.${createdFile.note}`,
              exportKind === 'gsheet' ? 'Open sheet' : 'Open doc',
              { duration: 10000 },
            )
            .onAction()
            .subscribe(() => window.open(createdFile.url, '_blank', 'noopener'));
        },
        error: (exportError: unknown) => {
          this.exporting.set(false);
          const failureReason =
            exportError instanceof HttpErrorResponse ? exportError.error?.reason : undefined;
          if (GOOGLE_RECONNECT_REASONS.includes(failureReason)) {
            // Connection missing or revoked: the toast action is a fresh gesture, so
            // it can open the consent popup and retry.
            this.googleConnected.set(false);
            this.snackBar
              .open(`Connect your Google account to export to ${exportLabel}.`, 'Connect Google', {
                duration: 10000,
              })
              .onAction()
              .subscribe(() => this.connectGoogleThenExport(exportKind, roundId, email));
            return;
          }
          this.snackBar.open(this.googleExportErrorMessage(exportKind, exportError), 'Dismiss', {
            duration: 6000,
          });
        },
      });
  }

  /** A user-facing message for a failed Google connect/export. */
  private googleExportErrorMessage(exportKind: GoogleExportKind, googleError: unknown): string {
    if (googleError instanceof GoogleAuthCodeError) {
      return googleError.message;
    }
    if (googleError instanceof HttpErrorResponse && typeof googleError.error?.message === 'string') {
      return googleError.error.message;
    }
    return `Could not export to ${this.exportFormatLabels[exportKind]}. Please try again.`;
  }

  /**
   * Exports every feedback item matching the current filters, in the current
   * sort order, as a CSV download. The feedback endpoint is paginated, so this
   * walks all pages (page 1 reveals the page count; the rest are fetched in
   * parallel and concatenated in page order, preserving the server's sort).
   */
  private exportCsv(): void {
    const roundId = this.id();
    if (!roundId || this.exporting()) {
      return;
    }
    this.exporting.set(true);
    this.snackBar.open('Preparing CSV export…', undefined, { duration: 2000 });

    this.fetchAllFilteredFeedback(roundId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (items) => {
          this.exporting.set(false);
          const isLinkExport = this.filters().category === FeedbackCategory.BrokenLinks;
          // Only Broken links exported: one row per link, laid out to action.
          const exportTable = isLinkExport
            ? this.buildLinkExportTable(items)
            : this.buildFeedbackExportTable(items);
          const [, ...exportRows] = exportTable;
          if (!exportRows.length) {
            const noMatchesMessage = isLinkExport
              ? 'No broken links match the current filters.'
              : 'No feedback matches the current filters.';
            this.snackBar.open(noMatchesMessage, 'Dismiss', { duration: 4000 });
            return;
          }
          this.downloadCsv(this.exportFilename(this.round()), toCsv(exportTable));
          const itemNoun = isLinkExport ? 'broken link' : 'feedback item';
          this.snackBar.open(
            `Exported ${exportRows.length} ${itemNoun}${exportRows.length === 1 ? '' : 's'} to CSV.`,
            'Dismiss',
            { duration: 4000 },
          );
        },
        error: () => {
          this.exporting.set(false);
          this.snackBar.open('Could not export the feedback. Please try again.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }

  /**
   * Fetches all pages of feedback for the current filters/sort and returns them
   * in order. Page 1 reveals `lastPage`; pages 2..lastPage are fetched together
   * and concatenated after page 1 so the server's sort order is preserved.
   */
  private fetchAllFilteredFeedback(roundId: string): Observable<Feedback[]> {
    const queryFilters = this.currentFeedbackQueryFilters();
    const requestPage = (page: number): Observable<Paginated<Feedback>> =>
      this.feedback.listFeedback({ feedbackRoundId: roundId, page, ...queryFilters });

    return requestPage(1).pipe(
      switchMap((firstPage) => {
        if (firstPage.lastPage <= 1) {
          return of(firstPage.results);
        }
        const remainingPages: Observable<Paginated<Feedback>>[] = [];
        for (let page = 2; page <= firstPage.lastPage; page++) {
          remainingPages.push(requestPage(page));
        }
        return forkJoin(remainingPages).pipe(
          map((pages) => [
            ...firstPage.results,
            ...pages.flatMap((pageResult) => pageResult.results),
          ]),
        );
      }),
    );
  }

  /**
   * The table's active filters and sort as feedback-list query fields, shared by
   * every export so each matches what the table shows.
   */
  private currentFeedbackQueryFilters(): Omit<
    ListFeedbackQuery,
    'feedbackRoundId' | 'withStubs' | 'page' | 'perPage'
  > {
    const activeFilters = this.filters();
    return {
      sort: this.sort()
        .map((spec) => `${spec.col}:${spec.dir}`)
        .join(','),
      url: activeFilters.url,
      category: activeFilters.category as FeedbackCategory | undefined,
      screen: activeFilters.screen as ScreenType | undefined,
      status: toStatusFilter(activeFilters.status),
      type: activeFilters.type as FeedbackType | undefined,
      score: activeFilters.score as FeedbackScoreBand | undefined,
      user: activeFilters.user,
      ...toCreatedRange(activeFilters),
    };
  }

  /**
   * The export when only Broken links are exported (header row first): one row per
   * link — the page it's on, the broken URL, its anchor text and what's wrong — with
   * the same tracking columns. The link checker's run-level items (5xx summary,
   * unreadable pages, a clean run) aren't about one link, so they're left out.
   * Mirrors the API's `LinkExportTable` (Google Sheets) column for column.
   */
  private buildLinkExportTable(items: Feedback[]): string[][] {
    const headers = [
      'Created',
      'Status',
      'Screen',
      'Created By',
      'Page URL',
      'Broken URL',
      'Anchor Text',
      'Issue',
      // Left empty for the team's own notes.
      'Notes',
    ];
    const runLevelFlags = ['linkCheckSummary', 'linkCheckUnreadable', 'linkCheckClean'];
    const linkItems = items.filter(
      (item) => !runLevelFlags.some((runLevelFlag) => Boolean(item.meta?.[runLevelFlag])),
    );
    const rows = linkItems.map((linkItem) => {
      const linkMeta = linkItem.meta ?? {};
      return [
        this.exportDate(linkItem.createdAt),
        linkItem.status ?? '',
        this.screenFor(linkItem.meta),
        this.exportCreatedBy(linkItem),
        linkItem.fullUrl ?? linkItem.url ?? '',
        this.exportBrokenUrl(linkMeta),
        this.exportAnchorText(linkItem, linkMeta),
        this.exportLinkIssue(linkMeta),
        '',
      ];
    });
    return [headers, ...rows];
  }

  /** Where the link points: the checked URL, or the placeholder href (a placeholder's linkTarget is only a de-dupe key). */
  private exportBrokenUrl(linkMeta: Record<string, unknown>): string {
    const placeholderType = linkMeta['linkPlaceholder'];
    if (typeof placeholderType === 'string') {
      if (placeholderType === 'missing_href') {
        return '(no href)';
      }
      if (placeholderType === 'empty_href') {
        return '(empty href)';
      }
      return typeof linkMeta['linkHref'] === 'string' ? linkMeta['linkHref'] : '';
    }
    return typeof linkMeta['linkTarget'] === 'string' ? linkMeta['linkTarget'] : '';
  }

  /** The link's text (an image's alt text); the element for items without link meta. */
  private exportAnchorText(linkItem: Feedback, linkMeta: Record<string, unknown>): string {
    if ('linkText' in linkMeta) {
      const linkText = typeof linkMeta['linkText'] === 'string' ? linkMeta['linkText'] : '';
      return linkText !== '' ? linkText : '(no text)';
    }
    return linkItem.element ?? '';
  }

  /** What's wrong: the HTTP status (e.g. "404 Not Found"), or why a placeholder goes nowhere. */
  private exportLinkIssue(linkMeta: Record<string, unknown>): string {
    const placeholderIssues: Record<string, string> = {
      missing_href: 'Placeholder link (no href)',
      empty_href: 'Placeholder link (empty href)',
      hash: 'Placeholder link (# only)',
      javascript: 'Placeholder link (JavaScript)',
    };
    const placeholderType = linkMeta['linkPlaceholder'];
    if (typeof placeholderType === 'string') {
      return placeholderIssues[placeholderType] ?? placeholderIssues['javascript'];
    }
    if (linkMeta['linkLocalhost']) {
      return linkMeta['linkKind'] === 'image' ? 'Localhost image' : 'Localhost link';
    }
    const httpStatus = Number(linkMeta['linkStatus']);
    if (!linkMeta['linkStatus'] || Number.isNaN(httpStatus)) {
      return '';
    }
    // Reason phrases for the statuses the checker reports (the API's LinkCheckFeedbackWriter::STATUS_REASONS).
    const statusReasons: Record<number, string> = { 404: 'Not Found', 410: 'Gone' };
    const statusText = `${httpStatus} ${statusReasons[httpStatus] ?? ''}`.trim();
    return linkMeta['linkKind'] === 'image' ? `Broken image (${statusText})` : statusText;
  }

  /**
   * The export as a table (header row first) in the design's column spec,
   * shared by every export format.
   */
  private buildFeedbackExportTable(items: Feedback[]): string[][] {
    const headers = [
      'Created',
      'Status',
      'Category',
      'Screen',
      'Created By',
      'URL',
      'Element',
      'Observation',
      'Recommendation',
      // Left empty for the team's own notes.
      'Notes',
      'Screenshot URL',
    ];
    const rows = items.map((item) => [
      this.exportDate(item.createdAt),
      item.status ?? '',
      item.category ?? '',
      this.screenFor(item.meta),
      this.exportCreatedBy(item),
      item.url ?? item.fullUrl ?? '',
      item.element ?? '',
      item.observation ?? '',
      item.recommendation ?? '',
      '',
      item.screenshotUrl ?? '',
    ]);
    return [headers, ...rows];
  }

  /**
   * The author shown in the export: AI-generated feedback is attributed to the
   * AI reviewer; everything else uses the recorded name (or email) from
   * `createdBy`, which may be a plain string or a `{ name, email }` object.
   */
  private exportCreatedBy(item: Feedback): string {
    if (item.type === FeedbackType.AiFeedback) {
      return 'AI Reviewer';
    }
    const createdBy = item.createdBy;
    if (typeof createdBy === 'string') {
      return createdBy;
    }
    if (createdBy && typeof createdBy === 'object') {
      const record = createdBy as Record<string, unknown>;
      const name = typeof record['name'] === 'string' ? record['name'] : '';
      const email = typeof record['email'] === 'string' ? record['email'] : '';
      return name || email || '';
    }
    return '';
  }

  /**
   * The date created, "YYYY-MM-DD", from the stored ISO timestamp (UTC, as
   * recorded). Falls back to the raw value if it isn't ISO.
   */
  private exportDate(createdAt: string | undefined): string {
    if (!createdAt) {
      return '';
    }
    const isoDateMatch = createdAt.match(/^\d{4}-\d{2}-\d{2}/);
    return isoDateMatch ? isoDateMatch[0] : createdAt;
  }

  /** A safe, descriptive download filename for the round's CSV. */
  private exportFilename(round: FeedbackRound | null): string {
    const base = round?.projectName || round?.projectId || 'feedback';
    const safe =
      base
        .trim()
        .replace(/[^\w.-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'feedback';
    return `${safe}-feedback.csv`;
  }

  /** Triggers a client-side download of the CSV text (UTF-8 with BOM for Excel). */
  private downloadCsv(filename: string, csv: string): void {
    // The BOM (U+FEFF) makes Excel open the file as UTF-8, not the system locale.
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  }

  /** Whether the upload-mockups drawer is open. */
  protected readonly uploadMockupsOpen = signal(false);

  /** Opens the upload-mockups drawer. */
  protected onUploadMockups(): void {
    this.uploadMockupsOpen.set(true);
  }

  /** Whether the add-asset drawer is open. */
  protected readonly addAssetOpen = signal(false);

  /** A newly added asset joins the round's list on screen; the API already stored it. */
  protected onAssetAdded(addedAsset: FeedbackRoundAsset): void {
    this.round.update((current) =>
      current ? { ...current, assets: [...(current.assets ?? []), addedAsset] } : current,
    );
    this.snackBar.open(`Added "${addedAsset.title}".`, 'Dismiss', { duration: 4000 });
  }

  /** Assets whose removal is in flight (their remove buttons are disabled). */
  protected readonly removingAssetIds = signal<string[]>([]);

  /** Takes an asset off the round, and off the list once the API has done it. */
  protected onRemoveAsset(assetToRemove: FeedbackRoundAsset): void {
    const round = this.round();
    if (!round) {
      return;
    }
    this.removingAssetIds.update((assetIds) => [...assetIds, assetToRemove.id]);
    const stopRemoving = () =>
      this.removingAssetIds.update((assetIds) => assetIds.filter((assetId) => assetId !== assetToRemove.id));

    this.feedbackRounds
      .removeAsset(round.id, assetToRemove.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          stopRemoving();
          this.round.update((current) =>
            current
              ? { ...current, assets: (current.assets ?? []).filter((roundAsset) => roundAsset.id !== assetToRemove.id) }
              : current,
          );
          this.snackBar.open(`Removed "${assetToRemove.title}".`, 'Dismiss', { duration: 4000 });
        },
        error: () => {
          stopRemoving();
          this.snackBar.open(`Could not remove "${assetToRemove.title}".`, 'Dismiss', { duration: 5000 });
        },
      });
  }

  /** Whether the manual email dialog is open. */
  protected readonly manualEmailOpen = signal(false);

  /** Bumped after an email is sent, so the email log pulls the new entry. */
  protected readonly emailLogRefresh = signal(0);

  /** The signed-in user, recorded as the sender of a manual email. */
  protected readonly emailSender = computed(() => this.currentUser.author());

  /** The Project Team member whose email preferences are open, if any. */
  protected readonly preferencesMember = signal<PreferencesMember | null>(null);

  /** Closes the email dialog and reports who it went to. */
  protected onManualEmailSent(result: PagesReadyForReviewResult): void {
    this.manualEmailOpen.set(false);
    this.emailLogRefresh.update((count) => count + 1);

    const sentCount = result.accepted.length;
    const notSentCount = result.rejected.length;
    const message = `Email sent to ${sentCount} ${sentCount === 1 ? 'person' : 'people'}`
      + (notSentCount ? ` · ${notSentCount} not sent (see the email log)` : '');
    this.snackBar.open(message, 'Dismiss', { duration: 6000 });
  }

  /** Whether the embed-settings drawer is open, and whether a save is in flight. */
  protected readonly embedSettingsOpen = signal(false);
  protected readonly savingDomains = signal(false);

  /** Opens the embed-settings (allowed domains) drawer. */
  protected onOpenEmbedSettings(): void {
    this.embedSettingsOpen.set(true);
  }

  /** Persists the round's allowed embed domains, patching the local round. */
  protected onSaveAllowedDomains(allowedDomains: string[]): void {
    const round = this.round();
    if (!round) {
      return;
    }
    this.savingDomains.set(true);
    this.feedbackRounds
      .updateAllowedDomains(round.id, allowedDomains)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.round.update((current) =>
            current ? { ...current, allowedDomains: updated.allowedDomains } : updated,
          );
          this.savingDomains.set(false);
          this.embedSettingsOpen.set(false);
          this.snackBar.open('Allowed domains saved.', 'Dismiss', { duration: 3000 });
        },
        error: () => {
          this.savingDomains.set(false);
          this.snackBar.open('Could not save allowed domains. Please try again.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }

  /** Patches the local round's mockups after a page's mockup is (re)assigned or pages added. */
  protected onRoundPagesUpdated(round: FeedbackRound): void {
    this.round.update((current) =>
      current ? { ...current, mockupUrls: round.mockupUrls } : round,
    );
  }

  /** The Pages panel changed the round's pages: refresh the AI prompts' page options. */
  protected onPagesChanged(): void {
    this.aiPagesRefresh.update((refreshCount) => refreshCount + 1);
  }

  /** Status values offered in each page row's status chip. */
  protected readonly pageStatuses = Object.values(PageStatus);

  /**
   * Persists freshly-uploaded mockup URLs to the round and patches the local
   * round so the mockups panel reflects them immediately.
   */
  protected onMockupsUploaded(urls: string[]): void {
    const round = this.round();
    if (!round || !urls.length) {
      return;
    }
    this.feedbackRounds
      .addMockup(round.id, urls)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.round.update((current) =>
            current ? { ...current, mockupUrls: updated.mockupUrls } : updated,
          );
          this.snackBar.open(
            `${urls.length} mockup${urls.length === 1 ? '' : 's'} uploaded.`,
            'Dismiss',
            { duration: 4000 },
          );
        },
        error: () =>
          this.snackBar.open('Mockups uploaded but could not be saved to the round.', 'Dismiss', {
            duration: 5000,
          }),
      });
  }

  /** Closes the new-feedback drawer (ignored while a create is in flight). */
  protected onCloseNewFeedback(): void {
    if (!this.creatingFeedback()) {
      this.newFeedbackOpen.set(false);
    }
  }

  /**
   * Creates a feedback item from the drawer form. When a screenshot is attached,
   * it's uploaded to a signed URL first, then its object URL is stored on the
   * feedback. On success the drawer closes and the list refetches.
   */
  protected onSubmitNewFeedback(submission: NewFeedbackSubmission): void {
    const roundId = this.id();
    if (!roundId || this.creatingFeedback()) {
      return;
    }
    this.creatingFeedback.set(true);

    const screenshotUrl$: Observable<string | null> = submission.screenshot
      ? this.uploadScreenshot(roundId, submission.screenshot)
      : of(null);

    screenshotUrl$
      .pipe(
        switchMap((screenshotUrl) =>
          this.feedback.createFeedback({
            feedbackRoundId: roundId,
            createdBy: this.currentUser.author(),
            fullUrl: submission.url,
            element: submission.element || null,
            observation: submission.observation || null,
            recommendation: submission.recommendation || null,
            screenshotUrl,
            meta: { screen: submission.screen },
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.creatingFeedback.set(false);
          this.newFeedbackOpen.set(false);
          // This refetch will include the item the user just added — don't toast
          // it as "new feedback came in" (they get a "Feedback created" toast).
          this.suppressNextNewFeedbackToast = true;
          this.feedbackRefresh.update((tick) => tick + 1);
          // A new item changes the round's completion ratio — refresh the bar.
          this.refreshRoundProgress();
          this.snackBar.open('Feedback created.', 'Dismiss', { duration: 4000 });
        },
        error: () => {
          this.creatingFeedback.set(false);
          this.snackBar.open('Could not create the feedback. Please try again.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }

  /**
   * Requests a signed upload URL for the screenshot, PUTs the file directly to
   * it, and resolves to the stored object URL (the signed URL minus its query).
   */
  private uploadScreenshot(roundId: string, file: File): Observable<string> {
    const contentType = file.type || 'application/octet-stream';
    const filename = `${Date.now()}-${file.name.replace(/[^\w.\-]+/g, '_')}`;
    return this.files
      .getSignedUrl({ feedbackRoundId: roundId, filename, contentType })
      .pipe(
        switchMap((signedUrl) =>
          this.http
            .put(signedUrl, file, { headers: { 'Content-Type': contentType } })
            .pipe(map(() => signedUrl.split('?')[0])),
        ),
      );
  }

  /** Maps a domain Feedback object onto the drawer's presentational shape. */
  private toFeedbackDetail(item: Feedback): FeedbackDetail {
    const meta = (item.meta ?? {}) as Record<string, unknown>;
    const metaString = (key: string): string | undefined =>
      typeof meta[key] === 'string' ? (meta[key] as string) : undefined;

    return {
      id: item.id,
      element: item.element ?? undefined,
      url: item.url ?? undefined,
      fullUrl: item.fullUrl ?? undefined,
      status: item.status ?? undefined,
      type: item.type ?? undefined,
      category: item.category ?? undefined,
      // Stored as a 0–1 fraction; the drawer shows it on a 0–10 scale.
      score: item.score == null ? null : item.score * 10,
      screen: this.screenFor(item.meta),
      createdBy: this.toUser(item.createdBy),
      created: item.createdAt,
      observation: item.observation ?? undefined,
      recommendation: item.recommendation ?? undefined,
      reasoning: item.reasoning ?? undefined,
      browser: metaString('browser'),
      os: metaString('os'),
      device: metaString('device'),
      resolution: metaString('resolution'),
      appVersion: metaString('appVersion') ?? metaString('app_version'),
      widgetVersion: metaString('widgetVersion'),
      orientation: metaString('orientation'),
      assets: (item.fileUrls ?? []).map((url) => ({ name: this.fileName(url), url })),
      screenshotUrl: item.screenshotUrl ?? undefined,
    };
  }

  /** Normalizes the free-form `createdBy` field into a name/email pair. */
  private toUser(createdBy: unknown): FeedbackDetailUser | null {
    if (!createdBy) {
      return null;
    }
    if (typeof createdBy === 'string') {
      return { name: createdBy };
    }
    if (typeof createdBy === 'object') {
      const record = createdBy as Record<string, unknown>;
      const name = typeof record['name'] === 'string' ? record['name'] : undefined;
      const email = typeof record['email'] === 'string' ? record['email'] : undefined;
      if (name || email) {
        return { name, email };
      }
    }
    return null;
  }

  /** The trailing file name of a URL, for asset labels. */
  private fileName(url: string): string {
    try {
      const path = new URL(url).pathname;
      return decodeURIComponent(path.split('/').filter(Boolean).pop() ?? url);
    } catch {
      return url.split('/').filter(Boolean).pop() ?? url;
    }
  }

  /** Kicks off the API work for a completed AI prompt. */
  protected onAiReview(submission: AiPromptSubmission): void {
    const roundId = this.id();
    if (!roundId) {
      return;
    }
    const answers = submission.answers;
    const url = answers['url'] ?? '';
    const mockupUrl = answers['mockup'] ?? '';
    // Whoever starts an AI review is emailed when it finishes, and recorded in the AI review log.
    const email = this.currentUser.email() || undefined;

    switch (submission.prompt.id) {
      case 'basic':
        this.runReview(this.aiFeedback.basicReview({ feedbackRoundId: roundId, url, mockupUrl, email }));
        break;
      case 'guided':
        this.runReview(
          this.aiFeedback.guidedReview({
            feedbackRoundId: roundId,
            url,
            mockupUrl,
            computerInstructions: answers['computerInstructions'] || undefined,
            feedbackDirection: answers['feedbackDirection'] || undefined,
            email,
          }),
        );
        break;
      case 'advanced':
        this.runReview(
          this.aiFeedback.advancedReview({
            feedbackRoundId: roundId,
            url,
            mockupUrl,
            prompt: answers['prompt'] || undefined,
            email,
          }),
        );
        break;
      case 'linkCheck': {
        const target = siteCheckTarget(answers);
        this.runReview(
          this.aiFeedback.linkCheck({
            feedbackRoundId: roundId,
            ...target,
            exclusions: target.scope === 'sitemap' ? answers['exclusions'] || undefined : undefined,
            email,
          }),
        );
        break;
      }
      case 'copyCheck': {
        const target = siteCheckTarget(answers);
        this.runReview(
          this.aiFeedback.copyCheck({
            feedbackRoundId: roundId,
            ...target,
            englishVariant: answers['englishVariant'] ?? '',
            exclusions: target.scope === 'sitemap' ? answers['exclusions'] || undefined : undefined,
            email,
          }),
        );
        break;
      }
      case 'audit':
        this.startAudit(roundId, googleDocLinks(answers['copyDocuments']), googleDocLinks(answers['drdDocument'])[0]);
        break;
      default:
        this.snackBar.open(`${submission.prompt.title} isn’t available yet.`, 'Dismiss', {
          duration: 4000,
        });
    }
  }

  /**
   * Starts a feedback audit report, then watches it until the Google Doc is ready.
   * A missing Google connection — or one from before the audit needed to read Google
   * Docs — is offered as a "Connect Google" toast, whose click opens the consent
   * popup and retries.
   */
  private startAudit(roundId: string, copyDocuments: string[], drdDocument: string | undefined, justConnected = false): void {
    const email = this.currentUser.email();
    if (!email) {
      this.snackBar.open('Sign in again to run the feedback audit.', 'Dismiss', { duration: 5000 });
      return;
    }
    // Connect Google first when we already know it's needed. This runs inside the
    // user's submit, so the consent popup can open. Right after connecting we go
    // straight to the API: if a permission was declined, it says so.
    if (!justConnected && !this.googleReadyForAudit(copyDocuments.length > 0 || !!drdDocument)) {
      this.connectGoogleThenAudit(roundId, email, copyDocuments, drdDocument);
      return;
    }

    this.feedbackAudits
      .startAudit({ feedbackRoundId: roundId, email, copyDocuments, drdDocument })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (auditRun) => {
          this.snackBar.open(
            `Auditing ${auditRun.itemCount} feedback item${auditRun.itemCount === 1 ? '' : 's'}. This can take a while — we’ll email you the report link when it’s ready.`,
            'Dismiss',
            { duration: 6000 },
          );
          this.watchAudit(auditRun.id);
          this.aiActivityRefresh.update((count) => count + 1);
        },
        error: (auditError: unknown) => {
          const failureReason = auditError instanceof HttpErrorResponse ? auditError.error?.reason : undefined;
          if (GOOGLE_RECONNECT_REASONS.includes(failureReason)) {
            this.snackBar
              .open(this.httpErrorMessage(auditError, 'Connect your Google account to run the audit.'), 'Connect Google', {
                duration: 10000,
              })
              .onAction()
              .subscribe(() => this.connectGoogleThenAudit(roundId, email, copyDocuments, drdDocument));
            return;
          }
          this.snackBar.open(this.httpErrorMessage(auditError, 'Could not start the feedback audit. Please try again.'), 'Dismiss', {
            duration: 8000,
          });
        },
      });
  }

  /** Google's consent popup (requested synchronously, inside the toast click), then the audit. */
  private connectGoogleThenAudit(roundId: string, email: string, copyDocuments: string[], drdDocument: string | undefined): void {
    this.googleAuthCode
      .requestCode(email)
      .then((authorizationCode) => firstValueFrom(this.googleConnections.connect({ email, code: authorizationCode })))
      .then((googleConnection) => {
        this.rememberGoogleConnection(googleConnection);
        this.startAudit(roundId, copyDocuments, drdDocument, true);
      })
      .catch((connectError: unknown) => {
        this.snackBar.open(
          connectError instanceof GoogleAuthCodeError ? connectError.message : 'Could not connect Google. Please try again.',
          'Dismiss',
          { duration: 6000 },
        );
      });
  }

  /** Polls a running audit and offers the report (or the failure) when it finishes. */
  private watchAudit(runId: string): void {
    timer(AUDIT_POLL_INTERVAL_MS, AUDIT_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.feedbackAudits.getAudit(runId).pipe(catchError(() => of(null)))),
        takeWhile((auditRun: FeedbackAuditRun | null) => auditRun === null || (auditRun.status !== 'completed' && auditRun.status !== 'failed'), true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((auditRun) => {
        if (auditRun?.status === 'completed' && auditRun.documentUrl) {
          const documentUrl = auditRun.documentUrl;
          // Opened from the toast action: the click is a fresh gesture, so no popup block.
          this.snackBar
            .open('Your feedback audit report is ready.', 'Open report', { duration: 20000 })
            .onAction()
            .subscribe(() => window.open(documentUrl, '_blank', 'noopener'));
        } else if (auditRun?.status === 'failed') {
          this.snackBar.open(`The feedback audit failed: ${auditRun.failureMessage ?? 'unknown error'}`, 'Dismiss', {
            duration: 10000,
          });
        }
      });
  }

  /** The API's `message` from an error response, or the fallback. */
  private httpErrorMessage(httpError: unknown, fallbackMessage: string): string {
    return httpError instanceof HttpErrorResponse && typeof httpError.error?.message === 'string'
      ? httpError.error.message
      : fallbackMessage;
  }

  /** Subscribes to a review request, surfacing the ack message (or an error). */
  private runReview(review$: Observable<AiReviewAck>): void {
    review$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (ack) => {
        this.snackBar.open(
          ack.message || 'AI review started. Feedback will appear when it’s ready.',
          'Dismiss',
          { duration: 5000 },
        );
        this.aiActivityRefresh.update((count) => count + 1);
      },
      error: () =>
        this.snackBar.open('Could not start the AI review. Please try again.', 'Dismiss', {
          duration: 5000,
        }),
    });
  }

  /**
   * The standardized screen type for a feedback item. Prefers the server-set
   * `meta.screen` (mobile/tablet/desktop); falls back to deriving it from the
   * captured resolution width for records that predate the backfill.
   */
  private screenFor(meta: Record<string, unknown> | null | undefined): string {
    const screen = typeof meta?.['screen'] === 'string' ? (meta['screen'] as string) : '';
    if (screen) {
      return screen;
    }
    return this.screenFromResolution(meta);
  }

  /** Derives a screen type from the captured viewport resolution width. */
  private screenFromResolution(meta: Record<string, unknown> | null | undefined): string {
    const resolution = typeof meta?.['resolution'] === 'string' ? (meta['resolution'] as string) : '';
    const width = Number(resolution.match(/\d+/)?.[0] ?? 0);
    if (!width) {
      return 'desktop';
    }
    // Breakpoints mirror the backend App\Enums\ScreenType.
    if (width < 768) {
      return 'mobile';
    }
    if (width < 1024) {
      return 'tablet';
    }
    return 'desktop';
  }

  protected onStatusChange(nextStatus: string): void {
    const round = this.round();
    if (!round) {
      return;
    }
    const previousStatus = round.status;
    const next = nextStatus as FeedbackRoundStatus;
    if (next === previousStatus) {
      return;
    }

    this.round.update((current) => (current ? { ...current, status: next } : current));
    this.feedbackRounds
      .updateFeedbackRoundStatus(round.id, next)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.round.update((current) =>
            current ? { ...current, status: previousStatus } : current,
          );
          this.snackBar.open('Could not update the project status. Reverted.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }
}
